import { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import type { SectionKey } from '@/types';
import { ALL_SECTION_LABELS, ALL_SECTION_ORDER } from '@/types';

export default function SectionSelectionDialog({
  open,
  enabledSections,
  onClose,
  onSave,
}: {
  open: boolean;
  enabledSections: SectionKey[];
  onClose: () => void;
  onSave: (sections: SectionKey[]) => void;
}) {
  const [selected, setSelected] = useState<SectionKey[]>(enabledSections);

  useEffect(() => {
    if (open) setSelected(enabledSections);
  }, [open, enabledSections]);

  const toggle = (key: SectionKey) => {
    setSelected((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Sections in this issue</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose which blocks appear in this issue's preview, export, and print. Nothing is deleted — hidden
          sections just stay out of the published document.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
          <Button size="small" onClick={() => setSelected([...ALL_SECTION_ORDER])}>
            Select all
          </Button>
          <Button size="small" onClick={() => setSelected([])}>
            Deselect all
          </Button>
        </Stack>
        <Divider sx={{ mb: 1 }} />
        <Stack>
          {ALL_SECTION_ORDER.map((key) => (
            <FormControlLabel
              key={key}
              control={<Checkbox checked={selected.includes(key)} onChange={() => toggle(key)} />}
              label={ALL_SECTION_LABELS[key]}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            onSave(selected);
            onClose();
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
